import { Suspense, useEffect, useState, useMemo, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import {
  Environment,
  RenderTexture,
  Text,
  PerspectiveCamera,
  Html,
  AdaptiveDpr,
  PerformanceMonitor,
} from "@react-three/drei";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import BubbleCSM from "./BubbleCSM";
import * as THREE from "three";
import { AnimatedLineSection } from "./AnimatedLineSection";
import { Header } from "./Header";
import ServicesAccordion from "./ServicesAccordion";
import Impressum from "./Impressum";

const LINE_1 = "online marketing";
const LINE_2 = "bubbling";
const SIZE = 14;

// Suspense Fallback Component
const SuspenseFallback = () => {
  return (
    <Html fullscreen>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#0010CA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
      </div>
    </Html>
  );
};

// Scroll Notice Component
const ScrollNotice = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const containerStyle = {
    position: 'absolute',
    bottom: isMobile ? '40px' : '60px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '12px' : '16px',
    zIndex: 10,
    opacity: 0.8,
    transition: 'opacity 0.3s ease',
  };

  const textStyle = {
    color: '#FDFCFF',
    fontFamily: 'PPNeueMontreal, sans-serif',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '300',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  };

  const bubbleStyle = {
    width: isMobile ? '6px' : '8px',
    height: isMobile ? '6px' : '8px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #ffffff 0%, #f3f9ff 100%)',
    opacity: 0.7,
    animation: 'bubbleFloat 4s ease-in-out infinite',
  };

  const bubble2Style = {
    ...bubbleStyle,
    animationDelay: '0.5s',
    opacity: 0.5,
  };

  const bubble3Style = {
    ...bubbleStyle,
    animationDelay: '1s',
    opacity: 0.3,
  };

  return (
    <div style={containerStyle}>
      <div style={bubbleStyle}></div>
      <span style={textStyle}>Scrollen</span>
      <div style={bubble2Style}></div>
      <span style={textStyle}>Entdecken</span>
      <div style={bubble3Style}></div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const footerStyle = {
    backgroundColor: '#EEEEEE',
    color: '#111111',
    padding: isMobile ? '40px 24px 30px' : '60px 80px 40px',
    fontFamily: 'PPNeueMontreal, sans-serif',
    fontSize: isMobile ? '12px' : '14px',
    lineHeight: 1.6,
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: isMobile ? '30px' : '40px',
  };

  const sectionStyle = {
    marginBottom: isMobile ? '15px' : '20px',
  };

  const titleStyle = {
    fontSize: isMobile ? '16px' : '18px',
    fontWeight: '500',
    marginBottom: '15px',
    color: '#111111',
  };

  const textStyle = {
    color: '#111111',
    marginBottom: '8px',
  };

  const linkStyle = {
    color: '#111111',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  };

  const linkHoverStyle = {
    color: '#111111',
  };

  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        <div style={sectionStyle}>
          <h3 style={titleStyle}>Bubbling</h3>
          <p style={textStyle}>
            Ihre kreative Digitalagentur für innovative Lösungen und sprudelnde Ideen.
          </p>
          <p style={textStyle}>
            Wir verwandeln Visionen in digitale Realitäten.
          </p>
        </div>

        <div style={sectionStyle}>
          <h3 style={titleStyle}>Kontakt</h3>
          <p style={textStyle}>
            <a 
              href="mailto:info@bubbling.ch" 
              style={linkStyle}
              onMouseEnter={(e) => Object.assign(e.target.style, linkHoverStyle)}
              onMouseLeave={(e) => Object.assign(e.target.style, linkStyle)}
            >
              info@bubbling.ch
            </a>
          </p>
          <p style={textStyle}>
            Schweiz
          </p>
        </div>

        <div style={sectionStyle}>
          <h3 style={titleStyle}>Impressum</h3>
          <p style={textStyle}>
            <a 
              href="/impressum" 
              style={linkStyle}
              onMouseEnter={(e) => Object.assign(e.target.style, linkHoverStyle)}
              onMouseLeave={(e) => Object.assign(e.target.style, linkStyle)}
            >
              Impressum anzeigen
            </a>
          </p>
        </div>
      </div>

      <div style={{
        borderTop: '1px solid #333333',
        marginTop: isMobile ? '30px' : '40px',
        paddingTop: '20px',
        textAlign: 'center',
        color: '#999999',
        fontSize: isMobile ? '10px' : '12px',
      }}>
        <p style={{ margin: 0 }}>
          © 2025 Bubbling. Alle Rechte vorbehalten.
        </p>
      </div>
    </footer>
  );
};

// Main page component
const MainPage = () => {
  const [textShouldFall, setTextShouldFall] = useState(false);
  // Stable quality tier with hysteresis to avoid ugly drops on capable machines
  const [qualityTier, setQualityTier] = useState(() => {
    if (typeof window === 'undefined') return 'high';
    const isCoarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const pr = window.devicePixelRatio || 1;
    const pixels = window.innerWidth * window.innerHeight * pr * pr;
    if (isCoarse && pixels > 8_000_000) return 'med';
    return 'high';
  }); // 'high' | 'med' | 'low'
  const declineCountRef = useRef(0);
  const inclineCountRef = useRef(0);
  const lastChangeAtRef = useRef(0);
  const changeCooldownMs = 2000;

  const isMobileCoarse = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || /Mobi|Android/i.test(navigator.userAgent);
  }, []);

  const tierConfig = useMemo(() => {
    const pr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1.5;
    if (qualityTier === 'high') return { dpr: [1, Math.min(2.5, pr)], antialias: !isMobileCoarse, lowQuality: false };
    if (qualityTier === 'med') return { dpr: [1, 1.25], antialias: !isMobileCoarse, lowQuality: isMobileCoarse };
    return { dpr: [1, 1.0], antialias: false, lowQuality: true };
  }, [qualityTier, isMobileCoarse]);

  const handleBubbleExplosion = () => {
    setTextShouldFall(true);
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh' }}>
      <Header />
      
      {/* Hero Section with Canvas */}
      <div style={{ height: 'calc(100vh - 92.5px)', width: 'calc(100% - 16px)', maxHeight: 'calc(100vw)', marginInline: 'auto', position: 'relative', marginTop: '92.5px', borderRadius: '16px 16px 0 0', borderTopRightRadius: '10px', overflow: 'hidden', backgroundColor: '#0010CA' }}>
        <Canvas 
          style={{ height: '100%', width: '100%', marginInline: 'auto' }}
          gl={{ 
            antialias: tierConfig.antialias,
            powerPreference: "high-performance",
            toneMapping: THREE.NoToneMapping,
            clearColor: new THREE.Color('#0010CA'),
            depth: true,
            stencil: false,
            alpha: false,
          }}
          frameloop="always"
          dpr={tierConfig.dpr}
        >
          <PerspectiveCamera position={[0, 0, 50]} makeDefault />
          <PerformanceMonitor
            iterations={60}
            flipflops={6}
            onDecline={(api) => {
              const now = performance.now();
              if (now - lastChangeAtRef.current < changeCooldownMs) return;
              declineCountRef.current += 1;
              inclineCountRef.current = 0;
              // Only degrade after sustained declines
              if (declineCountRef.current >= 2) {
                setQualityTier((prev) => {
                  lastChangeAtRef.current = now;
                  if (prev === 'high') return 'med';
                  if (prev === 'med') return 'low';
                  return prev;
                });
                declineCountRef.current = 0;
              }
            }}
            onIncline={(api) => {
              const now = performance.now();
              if (now - lastChangeAtRef.current < changeCooldownMs) return;
              inclineCountRef.current += 1;
              declineCountRef.current = 0;
              // Recover faster than we degrade
              if (inclineCountRef.current >= 2) {
                setQualityTier((prev) => {
                  lastChangeAtRef.current = now;
                  if (prev === 'low') return 'med';
                  if (prev === 'med') return 'high';
                  return prev;
                });
                inclineCountRef.current = 0;
              }
            }}
          />
          <AdaptiveDpr />
          <Suspense fallback={<SuspenseFallback />}>
            <BubbleCSM onExplosion={handleBubbleExplosion} lowQuality={tierConfig.lowQuality} />
            <Typography textShouldFall={textShouldFall} />
            <Environment environmentIntensity={0.75} files="environment.exr" />
          </Suspense>
        </Canvas>
        <ScrollNotice />
      </div>
      
      {/* Your other section with the self-contained animation */}
      <div id="agentur">
        <AnimatedLineSection 
          color="#5952BB" 
          pathData="M 4407 66 C 4570 96 2497 -223 2173 1480 S 4193 913 6000 1000" 
          title="Agentur für Social Media, Webdesign & Performance Marketing" 
          description="Wo Kreativität sprudelt und Innovation fliesst. Wir sind die Agentur, die Ihre Marke zum Blubbern bringt und digitale Wellen schlägt. Wir kümmern uns um Ihren Online-Auftritt, damit Sie sich auf Ihre Kernkompetenzen konzentrieren können."
          textAlign="left"
        />
      </div>
      <div id="services">
        <ServicesAccordion />
      </div>
      <div id="kontakt">
        <Footer />
      </div>
    </div>
  );
};

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/impressum" element={<Impressum />} />
      </Routes>
    </Router>
  );
}

const Typography = ({ textShouldFall }) => {
  const { width, height } = useThree((state) => state.viewport);
  const vw = (size) => (width * size) / 100;
  const vh = (size) => (height * size) / 100;
  
  // Fixed spacing between LINE_1 and LINE_2 - consistent across all screen sizes
  const computedLine1 = vw(SIZE / 8);
  const computedLine2 = vw(SIZE);
  const MIN_LINE1_FONT_SIZE = 1.6; // world units
  const MIN_LINE2_FONT_SIZE = 9.0; // world units
  const line1FontSize = Math.max(computedLine1, MIN_LINE1_FONT_SIZE);
  const line2FontSize = Math.max(computedLine2, MIN_LINE2_FONT_SIZE);
  const fixedSpacing = line2FontSize * 0.5; // 30% of LINE_2 font size for consistent spacing
  const line1Y = -fixedSpacing; // LINE_1 positioned above LINE_2 with fixed spacing
  const line2Y = 0; // LINE_2 at center
  
  const line1Ref = useRef();
  const line2Ref = useRef();
  const targetPosition = useMemo(() => (textShouldFall ? new THREE.Vector3(0, -vh(120), 0) : new THREE.Vector3(0, line1Y, 0)), [textShouldFall, line1Y, vh]);
  const currentPos = useRef(new THREE.Vector3(0, line1Y, 0));
  const opacityRef = useRef(1);

  useFrame(() => {
    const lerpFactor = textShouldFall ? 0.02 : 0.03;
    currentPos.current.lerp(targetPosition, lerpFactor);
    if (line1Ref.current) {
      line1Ref.current.position.copy(currentPos.current);
      // update opacity via material
      const targetOpacity = textShouldFall ? 0.8 : 1;
      opacityRef.current = THREE.MathUtils.lerp(opacityRef.current, targetOpacity, 0.02);
      if (line1Ref.current.material) {
        line1Ref.current.material.transparent = true;
        line1Ref.current.material.opacity = opacityRef.current;
      }
    }
  });

  return (
    <mesh>
      <planeGeometry args={[width, height, 1]} />
      <meshBasicMaterial>
        <RenderTexture attach="map">
           {/* Set background to white to match the page */}
          <color attach="background" args={["#0010CA"]} />
          <Text
            ref={line1Ref}
            font="/PPNeueMontreal-Light.otf"
            fontSize={line1FontSize}
            position={[0, line1Y, 0]}
            color={"#FAF4D8"}
            opacity={1}
            transparent={true}
          >
            {LINE_1}
          </Text>
          <Text
            ref={line2Ref}
            font="/PPNeueMontreal-Medium.otf"
            fontSize={line2FontSize}
            position={[0, line2Y, 0]}
            color={"#FFFCEC"}
          >
            {LINE_2}
          </Text>
        </RenderTexture>
      </meshBasicMaterial>
    </mesh>
  );
};